import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function TermsUpdateModal({ onAccepted }) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    if (!accepted || saving) return;
    setSaving(true);
    try {
      await api.post("/terms/accept");
      onAccepted();
    } catch {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm"
      data-testid="terms-update-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5 }}
        className="w-full max-w-lg glass frame-pulse rounded-3xl p-8 sm:p-10 text-center"
      >
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/15 text-primary mb-5">
          <ScrollText size={26} />
        </span>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl leading-tight mb-3 pb-[0.06em]">
          {t("terms.updateTitle")}
        </h2>
        <p className="text-foreground/65 leading-relaxed mb-7">{t("terms.updateBody")}</p>

        <label className="flex items-start gap-3 text-sm text-foreground/70 leading-relaxed cursor-pointer text-left mb-7" data-testid="terms-update-label">
          <input
            type="checkbox"
            data-testid="terms-update-checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 flex-shrink-0 accent-primary rounded"
          />
          <span>
            {t("auth.acceptPrefix")}{" "}
            <Link to="/legal/cgu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="terms-update-cgu-link">{t("auth.acceptCgu")}</Link>{" "}
            {t("auth.acceptAnd")}{" "}
            <Link to="/legal/charte" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="terms-update-charte-link">{t("auth.acceptCharte")}</Link>.
          </span>
        </label>

        <button
          onClick={accept}
          disabled={!accepted || saving}
          data-testid="terms-update-accept-btn"
          className="btn-frost-orange w-full py-3.5 rounded-full font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={17} className="animate-spin" /> : null}
          {t("terms.updateAccept")}
        </button>
      </motion.div>
    </motion.div>
  );
}
